import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import SEO from "@/components/SEO";
import { BookOpen, ChevronRight, Clock } from "lucide-react";

const LABELS = { LGS: "LGS", TYT: "TYT", AYT: "AYT" };
const DESCRIPTIONS = {
  LGS: "Liselere Geçiş Sistemi çıkmış soruları. Türkçe, Matematik, Fen Bilimleri, T.C. İnkılap Tarihi, Din Kültürü ve İngilizce.",
  TYT: "Temel Yeterlilik Testi çıkmış soruları. Türkçe, Matematik, Fen Bilimleri, Sosyal Bilimler.",
  AYT: "Alan Yeterlilik Testi çıkmış soruları. Matematik, Fizik, Kimya, Biyoloji, Edebiyat, Tarih, Coğrafya, Felsefe.",
};

export default function PastExams({ examType: examTypeProp }) {
  const { year } = useParams();
  const examType = (examTypeProp || "").toUpperCase();
  const [data, setData] = useState(null);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!LABELS[examType]) return;
    setLoading(true);
    api.get(`${V1}/past-exams/${examType}/years`).then((r) => setYears(r.data || []));
    if (year) {
      api.get(`${V1}/past-exams/${examType}/${year}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [examType, year]);

  if (!LABELS[examType]) return <div className="p-16 text-center">Sınav tipi bulunamadı.</div>;

  const title = year ? `${examType} ${year} Çıkmış Soruları` : `${examType} Çıkmış Sorular Arşivi`;
  return (
    <div data-testid={`past-exams-${examType.toLowerCase()}`}>
      <SEO
        title={`${title} — SanoStudent`}
        description={`${examType} ${year || ""} çıkmış soruları ve konu dağılımı — ücretsiz online arşiv.`}
        canonical={year ? `/${examType.toLowerCase()}/cikmis-sorular/${year}` : `/${examType.toLowerCase()}/cikmis-sorular`}
      />
      <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-14 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
            <Link to="/" className="hover:text-primary">Ana Sayfa</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/cikmis-sorular" className="hover:text-primary">Çıkmış Sorular</Link>
            <ChevronRight className="h-3 w-3" />
            <span>{examType}</span>
            {year && (<><ChevronRight className="h-3 w-3" /><span>{year}</span></>)}
          </nav>
          <div className="overline text-accent mb-2 flex items-center gap-1"><BookOpen className="h-3 w-3" /> Ücretsiz Arşiv</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{title}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">{DESCRIPTIONS[examType]}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="text-sm font-medium text-muted-foreground mb-3">Yıla göre gezin</div>
          <div className="flex flex-wrap gap-2">
            {years.length === 0 && <div className="text-sm text-muted-foreground">Henüz veri yok.</div>}
            {years.map((y) => (
              <Link key={y.year} to={`/${examType.toLowerCase()}/cikmis-sorular/${y.year}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${String(y.year) === String(year) ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:border-primary"}`}
                data-testid={`year-${y.year}`}>
                {y.year} <span className="text-[10px] opacity-70">({y.count})</span>
              </Link>
            ))}
          </div>
        </div>

        {loading && <div className="text-center text-muted-foreground py-16">Yükleniyor…</div>}

        {data && !loading && (
          <>
            <div className="grid md:grid-cols-4 gap-3 mb-8">
              <MetricCard label="Toplam Soru" value={data.total} />
              <MetricCard label="Yıl" value={data.year} />
              <MetricCard label="Konu Sayısı" value={data.subjects.length} accent />
              <MetricCard label="Süre" value="Sınırsız" />
            </div>

            {data.subjects.length > 0 && (
              <div className="mb-8">
                <h2 className="font-display text-2xl font-bold mb-3">Konu Dağılımı</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {data.subjects.map((s) => (
                    <div key={s.subject} className="p-3 rounded-lg border border-border bg-card">
                      <div className="font-medium">{s.subject}</div>
                      <div className="text-xs text-muted-foreground">{s.count} soru</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.questions.length > 0 ? (
              <div>
                <h2 className="font-display text-2xl font-bold mb-4">Sorular</h2>
                <div className="space-y-4">
                  {data.questions.map((q, i) => (
                    <div key={q.id} className="p-4 rounded-lg border border-border bg-card">
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>#{i + 1}</span>
                        <span>· {q.subject}</span>
                        {q.topic && <span>· {q.topic}</span>}
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> ~1 dk</span>
                      </div>
                      <div className="mt-2 whitespace-pre-wrap">{q.text}</div>
                      {q.options && q.options.length > 0 && (
                        <div className="mt-3 space-y-1 text-sm">
                          {q.options.map((o) => (
                            <div key={o.key}><b>{o.key})</b> {o.text}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {data.total > data.questions.length && (
                  <div className="mt-6 text-center text-sm text-muted-foreground">
                    {data.total - data.questions.length} soru daha var. Deneme sınavı olarak çözmek için üye olun.
                  </div>
                )}
              </div>
            ) : (
              <div className="p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
                Bu yıl için henüz yayına alınmış soru yok.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const MetricCard = ({ label, value, accent }) => (
  <div className={`p-4 rounded-xl border ${accent ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}>
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-2xl font-display font-bold mt-1">{value}</div>
  </div>
);
